import { useState } from "react";
import { LineChart, BarChart } from "../../v6common/Charts";
import { Blk, Qm } from "./useBlock";
import { costSeries, dayLabel, nf, usd, periodText, minutes, DASH } from "./format";

const PER = 7;
const H3 = ({ children }) => <div className="pn-h" style={{ padding: "8px 0", border: 0 }}><h3 style={{ fontSize: 13.5 }}>{children}</h3></div>;
const costTick = (v) => "$" + (+v).toFixed(4).replace(/0+$/, "").replace(/\.$/, "");

function Pager({ page, pages, total, onPage }) {
    const from = total ? (page - 1) * PER + 1 : 0;
    return (
        <div className="pager">
            <span>{total ? `${from} - ${Math.min(page * PER, total)}` : "0"} / 총 {total}건</span>
            <span className="pg">
                <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}><i className="fa-solid fa-chevron-left"></i> 이전</button>
                {Array.from({ length: pages }, (_, i) => (
                    <button type="button" key={i} className={page === i + 1 ? "on" : ""} onClick={() => onPage(i + 1)}>{i + 1}</button>
                ))}
                <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>다음 <i className="fa-solid fa-chevron-right"></i></button>
            </span>
        </div>
    );
}

function CostBody({ rows: raw, keys, convs }) {
    const [page, setPage] = useState(1);
    const s = costSeries(raw, keys);
    const t = s.totals;
    const labels = keys.map(dayLabel);
    const convTotal = convs ? convs.reduce((a, b) => a + b, 0) : null;
    const rows = keys
        .map((k, i) => ({ k, n: convs ? convs[i] : null, tok: s.llmTok[i], llm: s.llmCost[i], stt: s.stt[i], cost: s.cost[i] }))
        .filter((r) => r.n > 0 || r.tok > 0 || r.cost > 0)
        .reverse();
    const pages = Math.max(1, Math.ceil(rows.length / PER));
    const cur = Math.min(page, pages);
    const pRows = rows.slice((cur - 1) * PER, cur * PER);
    return (
        <>
            <div className="pn grid4">
                <Qm n={`${nf(t.embTok)} 토큰 / ${usd(t.embCost)}`} l="임베딩" f="지식베이스 문서 벡터화 · 문서 올릴 때 1회만 발생" />
                <Qm n={`${nf(t.llmTok)} 토큰 / ${usd(t.llmCost)}`} l="LLM API"
                    f={`대화 ${convTotal === null ? DASH : nf(convTotal)}건`} />
                <Qm n={`${minutes(t.sttSec)} / ${usd(t.sttCost)}`} l="음성 API" f="음성 입력 받아쓰기 시간" />
                <Qm n={usd(t.cost)} l="총 API 비용" f="임베딩 + LLM + 음성" />
            </div>
            <div className="cols2" style={{ padding: "0 20px 16px" }}>
                <div><H3>일별 LLM 토큰</H3>
                    <LineChart values={s.llmTok} labels={labels} name="LLM 토큰" height={200} fmt={(v) => (v >= 1000 ? v / 1000 + "k" : nf(v))} /></div>
                <div><H3>일별 API 비용</H3>
                    <BarChart values={s.cost.map((v) => +v.toFixed(4))} labels={labels} name="비용" height={200} color="#7c3aed" dec fmt={costTick} /></div>
            </div>
            <div style={{ padding: "0 20px 16px" }}><H3>날짜별 API 사용 내역</H3>
                <div className="rep-scroll"><table className="tbl">
                    <thead><tr><th>날짜</th><th className="num">대화</th><th className="num">LLM 토큰</th><th className="num">LLM 비용</th><th className="num">음성 비용</th><th className="num">총 비용</th></tr></thead>
                    <tbody>
                        {pRows.length ? pRows.map((r) => (
                            <tr key={r.k}>
                                <td className="c-dt">{r.k}</td>
                                <td className="num">{r.n === null ? DASH : `${r.n}건`}</td>
                                <td className="num">{nf(r.tok)}</td>
                                <td className="num">{usd(r.llm)}</td>
                                <td className="num">{usd(r.stt)}</td>
                                <td className="num"><b>{usd(r.cost)}</b></td>
                            </tr>
                        )) : <tr><td colSpan={6}><div className="empty">데이터가 없습니다</div></td></tr>}
                    </tbody>
                </table></div>
                <Pager page={cur} pages={pages} total={rows.length} onPage={setPage} />
            </div>
        </>
    );
}

export default function CostPanel({ days, keys, cost, convs }) {
    return (
        <div className="panel" role="region" aria-label="AI API 사용량 및 비용">
            <div className="pn-h"><h3>AI API 사용량 및 비용</h3><span className="hint">{periodText(days)}</span></div>
            <Blk st={cost}>{(rows) => <CostBody rows={rows} keys={keys} convs={convs} />}</Blk>
            <div className="ai" style={{ margin: "0 20px 20px" }}>
                <div className="ai-h"><i className="fa-solid fa-circle-info"></i>API 비용 정보</div>
                <ul>
                    <li><b>임베딩 비용</b>: 지식베이스에 파일을 등록할 때 문서를 벡터화하는 과정에서 발생 (1회성)</li>
                    <li><b>LLM API 비용</b>: 챗봇이 검색 결과를 바탕으로 답변을 만들 때 발생 (GPT-4o-mini 기준, 입력 $0.15 · 출력 $0.60 / 100만 토큰)</li>
                    <li><b>음성 API 비용</b>: 음성 입력을 글자로 바꾸는 비용 (받아쓰기 시간 기준)</li>
                </ul>
            </div>
        </div>
    );
}
